import { api } from "@/api";
import { QueryProps } from "@/types/Api";
import {
  BalanceTransaction,
  BalanceTransactionType,
  TransactionActorKind,
  TransactionRefType,
  TransactionStatus,
} from "@/types/ApiResponses/Transaction";
import { CouponStatus } from "@/types/ApiResponses/Coupon";

interface RawTransactionUser {
  id: number;
  username: string;
  role: string;
}

interface RawTransaction {
  id: number;
  targetUserId: number;
  performedBy: number | null;
  ref: string | null;
  refType: string | null;
  type: string;
  previousBalance: number | string;
  newBalance: number | string;
  amount: number | string;
  status: string;
  notes: string | null;
  balanceType: "credit" | "money" | null;
  createdAt: string;
  updatedAt: string;
  targetUser: RawTransactionUser;
  performedByUser: RawTransactionUser | null;
  couponStatus: CouponStatus | null;
}

// The dealer exchange types keep the server's names as enum values.
export const EXCHANGE_TRANSACTION_TYPES: readonly BalanceTransactionType[] = [
  BalanceTransactionType.ExchangeReserve,
  BalanceTransactionType.ExchangeRelease,
  BalanceTransactionType.ExchangePayout,
  BalanceTransactionType.ExchangeAdjustment,
  BalanceTransactionType.ExchangeCreditSpent,
  BalanceTransactionType.ExchangeCreditRefunded,
  BalanceTransactionType.ExchangeCreditRefundedReversal,
];

export const mapTransactionType = (type: string): BalanceTransactionType => {
  if (type === "balance_added") return BalanceTransactionType.Add;
  if (type === "balance_removed") return BalanceTransactionType.Remove;
  if (type === "balance_set") return BalanceTransactionType.Set;
  if (type === "bet_placed") return BalanceTransactionType.BetPlaced;
  if (type === "bet_won") return BalanceTransactionType.BetWon;
  if (type === "bet_refunded") return BalanceTransactionType.BetRefunded;
  if (type === "bet_cashed_out") return BalanceTransactionType.BetCashedOut;
  if (type === "bet_credit_spent") return BalanceTransactionType.BetCreditSpent;
  if (type === "bet_credit_refunded")
    return BalanceTransactionType.BetCreditRefunded;
  if (type === "game_stake") return BalanceTransactionType.GameStake;
  if (type === "game_payout") return BalanceTransactionType.GamePayout;
  if (type === "game_refund") return BalanceTransactionType.GameRefund;
  if (type === "game_payout_reversal")
    return BalanceTransactionType.GamePayoutReversal;
  if (type === "game_stake_funded")
    return BalanceTransactionType.GameStakeFunded;
  if (type === "game_payout_funded")
    return BalanceTransactionType.GamePayoutFunded;
  if (type === "game_refund_funded")
    return BalanceTransactionType.GameRefundFunded;
  if (type === "game_credit_spent")
    return BalanceTransactionType.GameCreditSpent;
  if (type === "game_credit_refunded")
    return BalanceTransactionType.GameCreditRefunded;
  const exchangeType = EXCHANGE_TRANSACTION_TYPES.find(
    (member) => member === type
  );
  if (exchangeType) return exchangeType;
  return type as BalanceTransactionType;
};

/**
 * Who stands behind a ledger row, as a fact rather than a label — the table
 * decides the wording.
 *
 * A row with no performer used to fall back to the coupon owner's username,
 * which read as "the player did this" on rows the player never touched (the
 * credit mirror rows, and cancellations written before sinek-api migration 116
 * started recording the actor). Nobody is named unless the ledger names them.
 */
const resolveActor = (
  tx: RawTransaction
): { addedBy: string; addedByKind: TransactionActorKind } => {
  if (tx.performedByUser?.username) {
    return { addedBy: tx.performedByUser.username, addedByKind: "user" };
  }
  // A manually cancelled coupon was closed by a person by definition; an empty
  // actor there means the row predates the actor column, not "the system".
  if (tx.type === "bet_refunded" && tx.couponStatus === CouponStatus.Canceled) {
    return { addedBy: "", addedByKind: "unknown" };
  }
  return { addedBy: "", addedByKind: "system" };
};

const mapTransaction = (tx: RawTransaction): BalanceTransaction => ({
  id: tx.id,
  type: mapTransactionType(tx.type),
  status: tx.status as TransactionStatus,
  amount: Number(tx.amount),
  previousBalance: Number(tx.previousBalance),
  newBalance: Number(tx.newBalance),
  currentBalance: Number(tx.newBalance),
  userName: tx.targetUser?.username ?? "-",
  couponStatus: tx.couponStatus ?? null,
  ...resolveActor(tx),
  user: tx.targetUser as unknown as BalanceTransaction["user"],
  createdAt: tx.createdAt,
  updatedAt: tx.updatedAt,
  ref: tx.ref ?? "",
  refType: (tx.refType ?? "") as TransactionRefType,
  notes: tx.notes ?? null,
  balanceType: tx.balanceType ?? null,
});

export const getBalanceTransactions = async (
  props: QueryProps
): Promise<BalanceTransaction[]> => {
  const res = await api.client.get("/users-new/transactions/paginated", {
    signal: props.signal,
    params: {
      page: 1,
      pageSize: 1000,
    },
  });
  const items: RawTransaction[] = res.data?.items ?? [];
  return items.map(mapTransaction);
};

// ---- Server-side paginated variant (admin transaction-history) ----

export interface BalanceTransactionsPaginationMeta {
  totalItems: number;
  itemCount: number;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
}

export interface BalanceTransactionsPaginatedParams {
  page: number;
  pageSize: number;
  q?: string;
  type?: string[];
  startDate?: string;
  endDate?: string;
}

export interface PaginatedBalanceTransactions {
  meta: BalanceTransactionsPaginationMeta;
  items: BalanceTransaction[];
}

// The table's type-filter values are the UI aliases ("add"); the server's
// TransactionType enum uses the ledger names ("balance_added"). Bet types
// already share the same value on both sides.
const UI_TO_SERVER_TYPE: Record<string, string> = {
  add: "balance_added",
  remove: "balance_removed",
  set: "balance_set",
};

// The admin transaction-history page shows for games only the
// equivalents of what it shows for bets. Bets show bet_placed,
// bet_refunded, bet_credit_spent and bet_credit_refunded, so the visible
// game types are game_stake, game_refund, game_credit_spent and
// game_credit_refunded. Every payout (bet_won, game_payout and their
// reversals) is hidden — a cash out (bet_cashed_out) is a payout too,
// so it is hidden with bet_won — and every funder-side row (the sub-dealer mirror
// rows game_stake_funded, game_payout_funded, game_refund_funded) is
// hidden because bets have no funder row. Enforced server-side so it
// can't be revealed via the type filter and pagination counts stay
// correct.
export const ADMIN_HISTORY_EXCLUDED_TYPES: readonly string[] = [
  "bet_won",
  "bet_won_reversal",
  "bet_cashed_out",
  "game_payout",
  "game_payout_reversal",
  "game_stake_funded",
  "game_payout_funded",
  "game_refund_funded",
  // The dealer exchange payout and its resettlement correction are
  // payout rows too (DEALER-EXCHANGE-PLAN v1.9, §12); the reserve,
  // release and credit rows stay visible.
  "exchange_payout",
  "exchange_adjustment",
];

// Only one page is fetched; search/type/date filters run on the server so the
// whole ledger is reachable (the legacy variant above is capped at the newest
// 1000 rows and stays for the small balance widgets).
export const getBalanceTransactionsPaginated = async (
  props: QueryProps<BalanceTransactionsPaginatedParams>
): Promise<PaginatedBalanceTransactions> => {
  const { type, ...rest } = props.params;
  const res = await api.client.get("/users-new/transactions/paginated", {
    signal: props.signal,
    params: {
      ...rest,
      type: type?.map((value) => UI_TO_SERVER_TYPE[value] ?? value),
      excludeType: ADMIN_HISTORY_EXCLUDED_TYPES,
    },
  });
  const items: RawTransaction[] = res.data?.items ?? [];
  return { meta: res.data.meta, items: items.map(mapTransaction) };
};
